import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@udm/contracts';
import {
  Building2,
  ChevronRight,
  ListChecks,
  Map as MapIcon,
  MapPin,
  Plus,
  Shapes,
  Square,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { canAny, createSite, deleteSite, updateSite } from '../../api/client';
import { deleteProject, fetchProjects } from '../../api/ops';
import { Field, Modal } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import { useMe } from '../auth/session';
import { useReportScope } from '../reports/ReportScope';
import { SerialListModal } from '../reports/SerialList';
import { decidesPlacement, PlacementReview } from './PlacementReview';
import { childKinds, SITES_KEY, useAdminSites } from './sitesData';
import './siteEditor.css';

type Site = api.Site;
type Kind = api.SiteKind;

export const SITE_ICON: Record<Kind, typeof Building2> = {
  organization: Building2,
  locality: MapPin,
  zone: Shapes,
  other: Square,
};

const TIME_ZONES = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['Europe/Bucharest', 'UTC'];
  }
})();

/** One titled block of the editor; the blocks flow in two columns on wide screens, one on phones. */
function Section({
  title,
  hint,
  wide = false,
  children,
}: {
  title: string;
  hint?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`site-editor-section${wide ? ' site-editor-wide' : ''}`}>
      <header>
        <h3>{title}</h3>
        {hint && <p className="muted small">{hint}</p>}
      </header>
      <div className="site-editor-body">{children}</div>
    </section>
  );
}

/**
 * Editor of a client, locality or zone (user request 2026-10-21: a large card over the list with a clear place for
 * every kind of information, instead of the narrow side drawer). Summary figures on top, then one section per topic:
 * general, administrative unit, map position, area and adoption, projects, localities and zones, command limit.
 */
export function SiteEditor({
  site,
  parent,
  subSites,
  deviceTotal,
  onClose,
  onCreated,
  onAddChild,
  onOpen,
  created = false,
}: {
  site?: Site;
  parent?: Site | null;
  /** the site's direct children with their device totals (subtree) */
  subSites: { site: Site; devices: number }[];
  /** devices of the site and everything below it */
  deviceTotal: number;
  onClose: () => void;
  onCreated: (s: Site) => void;
  onAddChild: (s: Site) => void;
  onOpen: (id: string) => void;
  created?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe().data;
  const creating = site === undefined;
  const kinds = childKinds(creating ? (parent?.kind ?? null) : null);
  const [form, setForm] = useState({
    kind: (site?.kind ?? kinds[0]) as Kind,
    name: site?.name ?? '',
    code: site?.code ?? '',
    user_prefix: site?.user_prefix ?? '',
    time_zone: site?.time_zone ?? parent?.time_zone ?? 'Europe/Bucharest',
    status: site?.status ?? 'active',
    lon: site?.lon?.toString() ?? '',
    lat: site?.lat?.toString() ?? '',
    uat_name: site?.uat_name ?? '',
    county: site?.county ?? '',
    siruta_code: site?.siruta_code ?? '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saved, setSaved] = useState(created);
  const readOnly = !creating && !site.can_manage;
  const isClient = form.kind === 'organization';
  // the client's administrative unit: the superadmin's, like the client's area (user request 2026-10-10)
  const uatEditable = isClient && (creating || site.can_edit_area);
  const sirutaValid = /^[0-9]{0,6}$/.test(form.siruta_code.trim());
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  };
  const refresh = () => qc.invalidateQueries({ queryKey: SITES_KEY });
  const coords = () => {
    const lon = form.lon.trim() === '' ? null : Number(form.lon);
    const lat = form.lat.trim() === '' ? null : Number(form.lat);
    return { lon: Number.isFinite(lon) ? lon : null, lat: Number.isFinite(lat) ? lat : null };
  };

  const save = useMutation({
    mutationFn: async () => {
      const common = { name: form.name, code: form.code || null, time_zone: form.time_zone, ...coords() };
      const prefix = isClient && form.user_prefix.length >= 2 ? { user_prefix: form.user_prefix } : {};
      const uat = {
        uat_name: form.uat_name.trim() || null,
        county: (form.county || null) as api.County | null,
        siruta_code: form.siruta_code.trim() || null,
      };
      const uatChanged =
        uatEditable &&
        (uat.uat_name !== (site?.uat_name ?? null) ||
          uat.county !== (site?.county ?? null) ||
          uat.siruta_code !== (site?.siruta_code ?? null));
      return creating
        ? createSite({
            ...common,
            ...prefix,
            ...(uatChanged ? uat : {}),
            parent_id: parent?.id ?? null,
            kind: form.kind,
          })
        : updateSite(site.id, {
            ...common,
            status: form.status,
            ...(form.user_prefix !== (site.user_prefix ?? '') ? prefix : {}),
            ...(uatChanged ? uat : {}),
          });
    },
    onSuccess: async (s) => {
      await refresh();
      setSaved(true);
      if (creating) onCreated(s);
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteSite(site!.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  const title = creating
    ? t(isClient ? 'sites.newClient' : 'sites.newSite', { kind: t(`sites.kind.${form.kind}`) })
    : site.name;
  const Icon = SITE_ICON[form.kind];

  return (
    <Modal
      title={
        <span className="site-editor-title">
          <Icon size={18} className="muted" /> {title}
        </span>
      }
      label={title}
      subtitle={
        creating
          ? parent
            ? t('sites.under', { name: parent.name })
            : t('sites.clientHint')
          : [
              t(`sites.kind.${site.kind}`),
              site.county,
              site.status === 'archived' ? t('sites.archived') : null,
            ]
              .filter(Boolean)
              .join(' · ')
      }
      onClose={onClose}
      footer={
        <div className="site-editor-foot">
          {saved && <span className="form-ok">{t('common.saved')}</span>}
          <span style={{ flex: 1 }} />
          {!readOnly && !creating && (
            <button type="button" className="btn btn-ghost btn-danger" onClick={() => setConfirmDelete(true)}>
              {t('common.delete')}
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending || form.name.trim() === '' || !sirutaValid}
              onClick={() => save.mutate()}
            >
              {creating ? t('common.create') : t('common.save')}
            </button>
          )}
        </div>
      }
    >
      <div className="site-editor">
        {readOnly && <p className="muted site-editor-note">{t('sites.readOnly')}</p>}
        {!creating && (
          <div className="kpi-row">
            <div className="card kpi">
              <span className="kpi-label">{t('sites.editor.devices')}</span>
              <span className="kpi-value">{formatNumber(deviceTotal)}</span>
            </div>
            <div className="card kpi">
              <span className="kpi-label">{t('sites.editor.children')}</span>
              <span className="kpi-value">{formatNumber(subSites.length)}</span>
            </div>
            <div className="card kpi">
              <span className="kpi-label">{t('area.section')}</span>
              <span className="site-editor-kpi-text">
                {site.has_area ? t('sites.editor.areaDrawn') : t('sites.editor.areaNone')}
              </span>
            </div>
            {site.kind === 'organization' && (
              <div className="card kpi">
                <span className="kpi-label">{t('area.autoAdopt')}</span>
                <span className="site-editor-kpi-text">
                  {site.auto_adopt ? t('sites.editor.on') : t('sites.editor.off')}
                </span>
              </div>
            )}
          </div>
        )}
        {confirmDelete && site && (
          <div className="confirm-box">
            <strong>{t('sites.deleteConfirm', { name: site.name })}</strong>
            <span className="muted">{t('sites.deleteHint')}</span>
            <ErrorNote error={remove.error} />
            <div className="toolbar">
              <button
                type="button"
                className="btn btn-danger-solid"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {t('common.delete')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
        <ErrorNote error={save.error} />
        <div className="site-editor-grid">
          <Section title={t('sites.editor.general')}>
            {creating && kinds.length > 1 && (
              <Field label={t('common.type')}>
                <select
                  className="input"
                  value={form.kind}
                  onChange={(e) => set('kind', e.target.value as Kind)}
                >
                  {kinds.map((k) => (
                    <option key={k} value={k}>
                      {t(`sites.kind.${k}`)}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label={t('common.name')} hint={isClient ? t('sites.clientNameHint') : undefined}>
              <input
                className="input"
                value={form.name}
                disabled={readOnly}
                maxLength={120}
                onChange={(e) => set('name', e.target.value)}
                autoFocus={creating}
              />
            </Field>
            {isClient && (
              <Field label={t('sites.userPrefix')} hint={t('sites.userPrefixHint')}>
                <input
                  className="input mono"
                  value={form.user_prefix}
                  disabled={readOnly}
                  maxLength={32}
                  placeholder={api.userPrefixFromName(form.name)}
                  onChange={(e) => set('user_prefix', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                />
              </Field>
            )}
            <div className="form-grid">
              <Field label={t('sites.code')} hint={t('sites.codeHint')}>
                <input
                  className="input"
                  value={form.code}
                  disabled={readOnly}
                  maxLength={40}
                  onChange={(e) => set('code', e.target.value)}
                />
              </Field>
              {!creating && (
                <Field label={t('common.status')}>
                  <select
                    className="input"
                    value={form.status}
                    disabled={readOnly}
                    onChange={(e) => set('status', e.target.value as 'active' | 'archived')}
                  >
                    <option value="active">{t('sites.active')}</option>
                    <option value="archived">{t('sites.archived')}</option>
                  </select>
                </Field>
              )}
            </div>
            <Field label={t('sites.timeZone')}>
              <select
                className="input"
                value={form.time_zone}
                disabled={readOnly}
                onChange={(e) => set('time_zone', e.target.value)}
              >
                {(TIME_ZONES.includes(form.time_zone) ? TIME_ZONES : [form.time_zone, ...TIME_ZONES]).map(
                  (z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ),
                )}
              </select>
            </Field>
          </Section>
          {isClient && (
            <Section title={t('sites.uat.title')} hint={t('sites.uat.hint')}>
              <Field label={t('sites.uat.name')}>
                <input
                  className="input"
                  value={form.uat_name}
                  disabled={!uatEditable}
                  maxLength={120}
                  placeholder={uatEditable ? form.name : t('sites.uat.none')}
                  onChange={(e) => set('uat_name', e.target.value)}
                />
              </Field>
              <div className="form-grid">
                <Field label={t('sites.uat.county')}>
                  <select
                    className="input"
                    value={form.county}
                    disabled={!uatEditable}
                    onChange={(e) => set('county', e.target.value)}
                  >
                    <option value="">{t('sites.uat.none')}</option>
                    {api.ROMANIAN_COUNTIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('sites.uat.siruta')}>
                  <input
                    className="input mono"
                    value={form.siruta_code}
                    disabled={!uatEditable}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={uatEditable ? undefined : t('sites.uat.none')}
                    onChange={(e) => set('siruta_code', e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </Field>
              </div>
            </Section>
          )}
          <Section title={t('sites.editor.position')} hint={t('sites.editor.positionHint')}>
            <div className="form-grid">
              <Field label={t('sites.latitude')}>
                <input
                  className="input"
                  inputMode="decimal"
                  value={form.lat}
                  disabled={readOnly}
                  onChange={(e) => set('lat', e.target.value)}
                  placeholder="47.30"
                />
              </Field>
              <Field label={t('sites.longitude')}>
                <input
                  className="input"
                  inputMode="decimal"
                  value={form.lon}
                  disabled={readOnly}
                  onChange={(e) => set('lon', e.target.value)}
                  placeholder="27.10"
                />
              </Field>
            </div>
          </Section>
          {!creating && <AreaSection site={site} />}
          {!creating && site.kind === 'organization' && canAny(me, 'project.view') && (
            <ProjectsSection client={site} />
          )}
          {!creating && (
            <Section title={t('sites.editor.children')}>
              {subSites.length === 0 ? (
                <span className="muted">{t('sites.editor.noChildren')}</span>
              ) : (
                <ul className="site-editor-list">
                  {subSites.map(({ site: c, devices }) => {
                    const ChildIcon = SITE_ICON[c.kind];
                    return (
                      <li key={c.id}>
                        <button type="button" className="site-editor-link" onClick={() => onOpen(c.id)}>
                          <ChildIcon size={15} className="muted" />
                          <span className="ellipsis">{c.name}</span>
                          <span className="kind-badge">{t(`sites.kind.${c.kind}`)}</span>
                          <span style={{ flex: 1 }} />
                          <span className="muted num">
                            {t('sites.devices', { formatted: formatNumber(devices) })}
                          </span>
                          <ChevronRight size={15} className="muted" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {site.can_add_child && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => onAddChild(site)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  <Plus size={15} /> {t('sites.addChild')}
                </button>
              )}
            </Section>
          )}
          {!creating && site.kind === 'organization' && site.can_edit_area && (
            <CommandLimitSection site={site} />
          )}
        </div>
      </div>
    </Modal>
  );
}

/** The client's projects (U20): every device of the client belongs to one of them. Managed in Reports → Projects. */
function ProjectsSection({ client }: { client: Site }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setScope = useReportScope((s) => s.set);
  const projects = useQuery({
    queryKey: ['projects', client.id],
    queryFn: () => fetchProjects(client.id),
  });
  const [serialsOf, setSerialsOf] = useState<api.Project | null>(null);
  const qc = useQueryClient();
  // only a project without devices can be deleted (user request 2026-10-22), never the default one
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
  const deletable = (p: api.Project) => p.can_manage && !p.is_default && p.devices === 0;
  return (
    <Section title={t('sites.editor.projects')} hint={t('sites.editor.projectsHint')} wide>
      {projects.isError ? (
        <ErrorNote error={projects.error} />
      ) : (
        <div className="ops-scroll">
          <table className="table table-tight">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th className="num">{t('sites.editor.devices')}</th>
                <th>{t('serials.serials')}</th>
                <th>{t('common.status')}</th>
                <th>{t('projects.baseline')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(projects.data?.items ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                    {p.is_default && (
                      <span className="pill" style={{ marginLeft: 6 }} title={t('projects.defaultHint')}>
                        {t('projects.default')}
                      </span>
                    )}
                  </td>
                  <td className="num">{formatNumber(p.devices)}</td>
                  <td>
                    {p.serials.listed ? formatNumber(p.serials.listed) : '—'}
                    {p.serials.not_installed > 0 && (
                      <span className="pill pill-warning" style={{ marginLeft: 6 }}>
                        {t('serials.notInstalled', { n: formatNumber(p.serials.not_installed) })}
                      </span>
                    )}
                  </td>
                  <td>{t(`projects.status.${p.status}`)}</td>
                  <td>
                    {p.baseline ? (
                      `${formatNumber(p.baseline.annual_value)} ${p.baseline.unit}`
                    ) : (
                      <span className="muted">{t('afm.noBaseline')}</span>
                    )}
                  </td>
                  <td className="right nowrap">
                    {confirmDelete === p.id ? (
                      <span className="toolbar" style={{ justifyContent: 'flex-end' }}>
                        <span className="small">
                          {t('projects.deleteConfirm', { name: p.name })}
                          {p.serials.listed > 0 && (
                            <span className="cell-sub">
                              {t('projects.deleteSerials', { n: formatNumber(p.serials.listed) })}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger-solid"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(p.id)}
                        >
                          {t('common.delete')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setConfirmDelete(null)}
                        >
                          {t('common.cancel')}
                        </button>
                      </span>
                    ) : (
                      <>
                        {(p.can_manage || p.serials.listed > 0) && (
                          <button type="button" className="btn btn-ghost" onClick={() => setSerialsOf(p)}>
                            {t('serials.list')}
                          </button>
                        )}
                        {deletable(p) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-danger"
                            onClick={() => {
                              remove.reset();
                              setConfirmDelete(p.id);
                            }}
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(projects.isLoading || projects.data?.items.length === 0) && (
                <tr>
                  <td colSpan={6} className="muted">
                    {projects.isLoading ? t('common.loading') : t('projects.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <button
        type="button"
        className="btn"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => {
          setScope({ clientId: client.id, projectId: null });
          void navigate('/reports/projects');
        }}
      >
        {t('sites.editor.openProjects')}
      </button>
      <ErrorNote error={remove.error} />
      {serialsOf && <SerialListModal project={serialsOf} onClose={() => setSerialsOf(null)} />}
    </Section>
  );
}

/**
 * The client's own command limit for its users (site administrator, operator), else the global default (Settings →
 * System). Superadmin only (user request 2026-10-10).
 */
function CommandLimitSection({ site }: { site: Site }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const own = site.command_rate_limit ?? null;
  const [form, setForm] = useState({ max: own?.max_actions ?? 30, window: own?.window_minutes ?? 10 });
  const save = useMutation({
    mutationFn: (v: api.CommandRateLimit | null) => updateSite(site.id, { command_rate_limit: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SITES_KEY }),
  });
  const int = (v: string, max: number) => Math.max(1, Math.min(max, Math.trunc(Number(v)) || 1));
  return (
    <Section
      title={t('sites.commandLimit.title')}
      hint={
        own
          ? t('sites.commandLimit.own', { max: own.max_actions, window: own.window_minutes })
          : t('sites.commandLimit.default')
      }
    >
      <div className="form-grid">
        <Field label={t('sites.commandLimit.max')}>
          <input
            className="input"
            type="number"
            min={1}
            max={1000}
            value={form.max}
            onChange={(e) => setForm({ ...form, max: int(e.target.value, 1000) })}
          />
        </Field>
        <Field label={t('sites.commandLimit.window')}>
          <input
            className="input"
            type="number"
            min={1}
            max={1440}
            value={form.window}
            onChange={(e) => setForm({ ...form, window: int(e.target.value, 1440) })}
          />
        </Field>
      </div>
      <div className="toolbar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate({ max_actions: form.max, window_minutes: form.window })}
        >
          {t('sites.commandLimit.save')}
        </button>
        {own && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={save.isPending}
            onClick={() => save.mutate(null)}
          >
            {t('sites.commandLimit.useDefault')}
          </button>
        )}
      </div>
      <ErrorNote error={save.error} />
    </Section>
  );
}

/** Area on the map (U12): status, editor link, placement review and, for clients, the auto-adoption switch. */
function AreaSection({ site }: { site: Site }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (on: boolean) => updateSite(site.id, { auto_adopt: on }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SITES_KEY }),
  });
  const isClient = site.kind === 'organization';
  const sites = useAdminSites();
  const me = useMe();
  const back = (useLocation().state as { placementReview?: string } | null)?.placementReview;
  const [review, setReview] = useState(back === site.id);
  const reviewBtn = useRef<HTMLButtonElement>(null);
  return (
    <Section title={t('area.section')} hint={site.has_area ? t('area.drawn') : t('area.none')}>
      <div className="toolbar">
        {site.can_edit_area && (
          <button type="button" className="btn" onClick={() => navigate(`/sites/${site.id}/area`)}>
            <MapIcon size={15} /> {site.has_area ? t('area.edit') : t('area.draw')}
          </button>
        )}
        {site.can_edit_area && site.has_area && (
          <button ref={reviewBtn} type="button" className="btn btn-ghost" onClick={() => setReview(true)}>
            <ListChecks size={15} /> {t('placement.check')}
          </button>
        )}
      </div>
      {review && (
        <PlacementReview
          siteId={site.id}
          sites={sites.data?.items ?? []}
          canDecide={decidesPlacement(me.data)}
          onClose={() => setReview(false)}
          returnFocus={reviewBtn}
        />
      )}
      {isClient && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={site.auto_adopt}
            disabled={!site.can_edit_area || toggle.isPending || (!site.has_area && !site.auto_adopt)}
            onChange={(e) => toggle.mutate(e.target.checked)}
          />
          <span>
            <strong>{t('area.autoAdopt')}</strong>
            <span className="field-hint" style={{ display: 'block' }}>
              {site.has_area ? t('area.autoAdoptHint') : t('area.autoAdoptNeedsArea')}
            </span>
          </span>
        </label>
      )}
      <ErrorNote error={toggle.error} />
    </Section>
  );
}
